import React from 'react';
import { useWatch } from 'react-hook-form';
import { Grid, Box, Typography, Alert } from '@mui/material';
import { NumberInput, SelectInput, FormDataConsumer, Labeled } from 'react-admin';
import DateTimeInputUTC from '../../../components/DateTimeInputUTC';
import { GiftExpirationType, GiftProductType, GiftRuleType } from '../../../models';
import { Tooltip } from '../../../components';

const FormLimitsExpiration: React.FC = () => {
  // Brinde com regra unique_code tem limites/expiração fixos:
  // limite = 1 resgate por cliente; expiração via agenda (startAt/endAt) do brinde.
  const giftRules = useWatch({ name: 'giftRules' }) || [];
  const products = useWatch({ name: 'products' }) || [];
  const hasUniqueCode = Array.isArray(giftRules) && giftRules.some((r: any) => r?.type === GiftRuleType.unique_code);
  const hasPredictionTicket = Array.isArray(products) && products.some((p: any) => p?.type === GiftProductType.PredictionTicket);
  const hasPredictionWinner = Array.isArray(giftRules) && giftRules.some((r: any) => r?.type === GiftRuleType.prediction_winner);
  // Número da Sorte não gera cupom: a validade de resgate não tem o que controlar.
  // O limite de CONQUISTA continua valendo — e cada conquista vale luckyNumberAmount números.
  const drawingNumberProduct = Array.isArray(products)
    ? products.find((p: any) => p?.type === GiftProductType.DrawingNumber)
    : undefined;
  const hasDrawingNumber = !!drawingNumberProduct;
  const luckyNumberAmount = drawingNumberProduct?.luckyNumberAmount ?? 1;
  // Giro na Roleta segue a mesma lógica do Número da Sorte: não gera cupom, e o
  // limite conta CONQUISTAS — cada conquista libera `spinAmount` giros.
  const spinTicketProduct = Array.isArray(products)
    ? products.find((p: any) => p?.type === GiftProductType.SpinTicket)
    : undefined;
  const hasSpinTicket = !!spinTicketProduct;
  const spinAmount = spinTicketProduct?.spinAmount ?? 1;

  if (hasUniqueCode) {
    return (
      <Box>
        <Typography variant="h6" fontWeight={600} mb={3}>
          Limites de Conquista e Validade do Brinde
        </Typography>
        <Alert
          severity="info"
          sx={{ bgcolor: '#e3f2fd', border: '1px solid #2196f3' }}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            🎟️ Controlados automaticamente pela regra de Divulgação
          </Typography>
          <Typography variant="body2">
            Brindes com regra de <strong>Divulgação</strong> têm limites fixos:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • <strong>Limite de conquista:</strong> 1 resgate por cliente (fixo, não configurável)<br/>
            • <strong>Validade da campanha:</strong> use os campos <em>"Data de Início"</em> e <em>"Data de Fim"</em> na aba de Agendamento
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (hasPredictionTicket || hasPredictionWinner) {
    return (
      <Box>
        <Typography variant="h6" fontWeight={600} mb={3}>
          Limites de Conquista e Validade do Brinde
        </Typography>
        <Alert severity="info" sx={{ bgcolor: '#ede9fe', border: '1px solid #7869DF' }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            ⚽ Controlados automaticamente pelo Bolão
          </Typography>
          <Typography variant="body2">
            {hasPredictionTicket
              ? 'Brindes com recompensa Direito ao Palpite têm hard-cap de 1 ticket por cliente por bolão. Validade casa com o prazo de palpite do bolão.'
              : 'Brindes com regra Acertou Bolão são disparados manualmente pelo admin do bolão. Cada cliente ganha no máximo 1 vez por bolão.'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • <strong>Limite de conquista:</strong> 1 por cliente por bolão (fixo)<br/>
            • <strong>Validade:</strong> controlada pelo bolão vinculado (kickoff/processamento)
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} mb={3}>
        Limites de Conquista e Validade do Brinde
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={3}>
        {hasSpinTicket
          ? 'Configure quantas vezes o cliente pode ganhar o brinde ao cumprir as regras. O limite conta CONQUISTAS; cada conquista libera os giros configurados na recompensa — não há cupom para resgatar depois.'
          : hasDrawingNumber
            ? 'Configure quantas vezes o cliente pode ganhar o brinde ao cumprir as regras. Cada conquista concede números da sorte na hora — não há cupom para resgatar depois.'
            : 'Configure quantas vezes o cliente pode ganhar o brinde ao cumprir as regras e por quanto tempo ele permanece válido para resgate.'}
      </Typography>

      <Labeled
        label={
          <Tooltip
            title={hasSpinTicket
              ? `Defina quantas vezes o cliente pode CONQUISTAR o brinde dentro do período. Cada conquista libera ${spinAmount} ${spinAmount === 1 ? 'giro' : 'giros'} na roleta.`
              : hasDrawingNumber
                ? `Defina quantas vezes o cliente pode CONQUISTAR o brinde dentro do período. Cada conquista concede ${luckyNumberAmount} ${luckyNumberAmount === 1 ? 'número da sorte' : 'números da sorte'}.`
                : 'Defina quantas vezes o cliente pode ganhar dentro do período de tempo especificado. Ex: 3 vezes a cada 24 horas.'}
            sxTooltipContainer={{ zIndex: 9 }}
          >
            Limite de conquista
          </Tooltip>
        }
        fullWidth
      >
        <Grid container spacing={2} sx={{ maxWidth: 800 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <NumberInput
              source="limitQuantity"
              label="Máximo de vezes a ganhar"
              min={1}
              fullWidth
              helperText={hasSpinTicket
                ? `Limita CONQUISTAS, não giros: cada conquista vale ${spinAmount} ${spinAmount === 1 ? 'giro' : 'giros'}. Deixe vazio para ilimitado`
                : hasDrawingNumber
                  ? `Limita CONQUISTAS, não números: cada conquista vale ${luckyNumberAmount} ${luckyNumberAmount === 1 ? 'número' : 'números'}. Deixe vazio para ilimitado`
                  : 'Deixe vazio para ilimitado'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <NumberInput
              source="limitIntervalInHours"
              label="A cada quantas horas"
              min={1}
              fullWidth
              helperText="Período para o limite (ex: 24h)"
            />
          </Grid>
        </Grid>
      </Labeled>

      {hasSpinTicket ? (
        <Alert severity="info" sx={{ mt: 3, bgcolor: '#ede9fe', border: '1px solid #7869DF' }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            🎡 Sem validade de resgate
          </Typography>
          <Typography variant="body2">
            A recompensa <strong>Giro na Roleta</strong> não gera cupom: o giro é liberado na hora da conquista e
            vale pelo prazo definido na própria roleta.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • <strong>Validade:</strong> controlada pela roleta (validade do giro e data de fim)<br/>
            • <strong>Limite de conquista:</strong> configurável acima — cada conquista libera{' '}
            {spinAmount} {spinAmount === 1 ? 'giro' : 'giros'}
          </Typography>
        </Alert>
      ) : hasDrawingNumber ? (
        <Alert severity="info" sx={{ mt: 3, bgcolor: '#e0f2f1', border: '1px solid #00897b' }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            🎲 Sem validade de resgate
          </Typography>
          <Typography variant="body2">
            A recompensa <strong>Número da Sorte</strong> não gera cupom: os números são concedidos na
            hora da conquista e valem até a data do sorteio vinculado.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • <strong>Validade:</strong> controlada pelo sorteio (data de encerramento e data do sorteio)<br/>
            • <strong>Limite de conquista:</strong> configurável acima — cada conquista concede{' '}
            {luckyNumberAmount} {luckyNumberAmount === 1 ? 'número' : 'números'}
          </Typography>
        </Alert>
      ) : (
        <Labeled
          label={
            <Tooltip
              title="Tempo que o cliente tem para resgatar o prêmio em um local de troca após ganhar o brinde."
              sxTooltipContainer={{ zIndex: 9 }}
            >
              Validade para resgate do prêmio
            </Tooltip>
          }
          fullWidth
          sx={{ mt: 3 }}
        >
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SelectInput
                source="expirationType"
                label="Tipo de validade"
                choices={GiftExpirationType.giftExpirationTypeChoices}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormDataConsumer>
                {({ formData }) => (
                  formData.expirationType === GiftExpirationType.fixed ? (
                    <DateTimeInputUTC
                      source="expirationTime"
                      label="Válido até"
                      isRequired
                      fullWidth
                      helperText="Data limite para resgatar o prêmio"
                    />
                  ) : formData.expirationType === GiftExpirationType.time ? (
                    <NumberInput
                      source="expirationInHours"
                      label="Válido por (horas)"
                      min={1}
                      isRequired
                      fullWidth
                      helperText="Horas após ganhar para resgatar"
                    />
                  ) : (
                    <Box />
                  )
                )}
              </FormDataConsumer>
            </Grid>
          </Grid>
        </Labeled>
      )}

      <Box mt={4} p={2.5} bgcolor="info.lighter" borderRadius={2} border="1px solid" borderColor="info.light">
        <Typography variant="body2" color="info.main" mb={1.5}>
          <strong>💡 Exemplo:</strong> 
        </Typography>
        {hasSpinTicket ? (
          <Typography variant="body2" color="info.main">
            Configurando 3 vezes a cada 24 horas, o cliente pode conquistar até 3 vezes por dia — ou seja,
            até {3 * spinAmount} {3 * spinAmount === 1 ? 'giro' : 'giros'} por dia.
          </Typography>
        ) : hasDrawingNumber ? (
          <Typography variant="body2" color="info.main">
            Configurando 3 vezes a cada 24 horas, o cliente pode conquistar até 3 vezes por dia — ou seja,
            até {3 * luckyNumberAmount} {3 * luckyNumberAmount === 1 ? 'número da sorte' : 'números da sorte'} por dia.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="info.main">
              Configurando 3 vezes a cada 24 horas, o cliente pode ganhar até 3 vezes por dia ao cumprir as regras.
            </Typography>
            <Typography variant="body2" color="info.main">
              A validade define quanto tempo após ganhar a recompensa ele pode ser resgatado em um local de troca.
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default FormLimitsExpiration;
